// src/Pages/Purchase/PurchaseData/VendorsData.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./VendorsData.css";

const countries = [
  "India",
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "UAE",
  "Singapore",
];
const indiaStates = [
  "Tamil Nadu",
  "Karnataka",
  "Maharashtra",
  "Delhi",
  "Gujarat",
  "Kerala",
  "Telangana",
  "Andhra Pradesh",
];
const currencies = [
  { code: "INR", name: "Indian Rupee" },
  { code: "USD", name: "United States Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "AED", name: "UAE Dirham" },
];
const languages = [
  "English",
  "Tamil",
  "Hindi",
  "French",
  "Spanish",
  "German",
  "Arabic",
  "Telugu",
  "Kannada",
  "Malayalam",
];
const tdsOptions = [
  "None",
  "Commission or Brokerage 2%",
  "Commission or Brokerage Reduced 3%",
  "Dividend 10%",
  "Dividend Reduced 7%",
  "Rent - 194I 10%",
  "Professional Fees 10%",
];

const VendorsData = () => {
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === "production"
      ? "https://finance.dev.marsforce.in"
      : "http://localhost:8030");
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = id && id !== "new";

  const [activeTab, setActiveTab] = useState("other");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const userId =
    localStorage.getItem("userId") || sessionStorage.getItem("userId");

  const [form, setForm] = useState({
    salutation: "Mr.",
    firstName: "",
    lastName: "",
    companyName: "",
    displayName: "",
    email: "",
    workPhone: "",
    mobile: "",
    language: "English",
    pan: "",
    currency: "INR",
    accountsPayable: "",
    openingBalance: "",
    paymentTerms: "Due on Receipt",
    tds: "",
    enablePortal: false,
    documents: [],
    remarks: "",
    billing: {
      attention: "",
      country: "India",
      street1: "",
      street2: "",
      city: "",
      state: "Tamil Nadu",
      pincode: "",
      phone: "",
      fax: "",
    },
    shipping: {
      attention: "",
      country: "India",
      street1: "",
      street2: "",
      city: "",
      state: "Tamil Nadu",
      pincode: "",
      phone: "",
      fax: "",
    },
    contacts: [
      {
        salutation: "Mr.",
        firstName: "",
        lastName: "",
        email: "",
        workPhone: "",
        mobile: "",
      },
    ],
    bankAccounts: [],
  });

  const [bankForm, setBankForm] = useState({
    holderName: "",
    bankName: "",
    accountNumber: "",
    reAccountNumber: "",
    ifsc: "",
  });
  const [showBankForm, setShowBankForm] = useState(false);
  const [bankError, setBankError] = useState("");

  // Load vendor
  useEffect(() => {
    if (isEdit && userId) {
      const loadVendor = async () => {
        try {
          setLoading(true);
          const res = await fetch(
            `${backendUrl}/api/vendors/get?userId=${userId}`
          );
          const result = await res.json();
          if (result.success) {
            const vendor = result.data.find((v) => v._id === id);
            if (vendor) {
              setForm({
                ...vendor,
                billing: vendor.billing || form.billing,
                shipping: vendor.shipping || form.shipping,
                contacts:
                  vendor.contacts?.length > 0 ? vendor.contacts : form.contacts,
                bankAccounts: vendor.bankAccounts || [],
                documents: vendor.documents || [],
              });
            }
          }
        } catch (err) {
          setError("Failed to load vendor");
        } finally {
          setLoading(false);
        }
      };
      loadVendor();
    }
  }, [id, isEdit, userId]);

  // Auto display name
  useEffect(() => {
    const display =
      `${form.salutation} ${form.firstName} ${form.lastName}`.trim();
    if (display && display !== "Mr." && display !== form.displayName) {
      setForm((prev) => ({ ...prev, displayName: display }));
    }
  }, [form.salutation, form.firstName, form.lastName]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddressChange = (type, e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [type]: { ...prev[type], [name]: value } }));
  };

  const copyBillingToShipping = () => {
    setForm((prev) => ({ ...prev, shipping: { ...prev.billing } }));
  };

  const addContact = () => {
    setForm((prev) => ({
      ...prev,
      contacts: [
        ...prev.contacts,
        {
          salutation: "Mr.",
          firstName: "",
          lastName: "",
          email: "",
          workPhone: "",
          mobile: "",
        },
      ],
    }));
  };

  const updateContact = (idx, field, value) => {
    setForm((prev) => {
      const newContacts = [...prev.contacts];
      newContacts[idx][field] = value;
      return { ...prev, contacts: newContacts };
    });
  };

  const removeContact = (idx) => {
    if (form.contacts.length === 1) return;
    setForm((prev) => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== idx),
    }));
  };

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankForm((prev) => ({ ...prev, [name]: value }));
  };

  const addBankAccount = () => {
    if (bankForm.accountNumber !== bankForm.reAccountNumber) {
      setBankError("Account numbers do not match");
      return;
    }
    if (!/^\d+$/.test(bankForm.accountNumber)) {
      setBankError("Account number must contain only digits");
      return;
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankForm.ifsc.toUpperCase())) {
      setBankError("Invalid IFSC code");
      return;
    }

    setForm((prev) => ({
      ...prev,
      bankAccounts: [
        ...prev.bankAccounts,
        { ...bankForm, ifsc: bankForm.ifsc.toUpperCase() },
      ],
    }));

    setBankForm({
      holderName: "",
      bankName: "",
      accountNumber: "",
      reAccountNumber: "",
      ifsc: "",
    });
    setShowBankForm(false);
    setBankError("");
  };

  const removeBankAccount = (idx) => {
    setForm((prev) => ({
      ...prev,
      bankAccounts: prev.bankAccounts.filter((_, i) => i !== idx),
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (form.documents.length + files.length > 10) {
      alert("Maximum 10 files allowed");
      return;
    }
    const validFiles = files.filter((f) => f.size <= 10 * 1024 * 1024);
    if (validFiles.length !== files.length)
      alert("Some files are larger than 10MB");
    setForm((prev) => ({
      ...prev,
      documents: [...prev.documents, ...validFiles.map((f) => f.name)],
    }));
  };

  const removeDocument = (i) => {
    setForm((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, idx) => idx !== i),
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError("");
    const payload = {
      ...form,
      userId,
      _id: isEdit ? id : undefined,
      payables: form.openingBalance || "0.00",
      unusedCredits: "0.00",
    };

    try {
      const res = await fetch(`${backendUrl}/api/vendors/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.success) {
        alert("Vendor saved successfully!");
        navigate("/purchase/vendors");
      } else {
        setError(result.error || "Failed to save");
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading vendor...</div>;

  return (
    <div className="vendordata-container">
      <div className="form-card">
        <h2>{isEdit ? "Edit Vendor" : "New Vendor"}</h2>
        {error && <div className="error-msg">{error}</div>}

        {/* Primary Contact & Other Fields (same as before) */}
        <div className="form-row">
          <label>
            Primary Contact <span className="req">*</span>
          </label>
          <div className="inline-fields">
            <select
              name="salutation"
              value={form.salutation}
              onChange={handleChange}
            >
              <option>Mr.</option>
              <option>Mrs.</option>
              <option>Ms.</option>
              <option>Miss</option>
              <option>Dr.</option>
            </select>
            <input
              type="text"
              placeholder="First Name"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
            />
            <input
              type="text"
              placeholder="Last Name"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <label>Company Name</label>
          <input
            type="text"
            name="companyName"
            value={form.companyName}
            onChange={handleChange}
          />
        </div>
        <div className="form-row">
          <label>
            Display Name <span className="req">*</span>
          </label>
          <input
            type="text"
            name="displayName"
            value={form.displayName}
            onChange={handleChange}
          />
        </div>
        <div className="form-row">
          <label>
            Email Address <span className="req">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
          />
        </div>
        <div className="form-row">
          <label>Phone</label>
          <div className="inline-fields">
            <input
              type="tel"
              placeholder="Work Phone"
              name="workPhone"
              value={form.workPhone}
              onChange={handleChange}
            />
            <input
              type="tel"
              placeholder="Mobile"
              name="mobile"
              value={form.mobile}
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="form-row">
          <label>Vendor Language</label>
          <select name="language" value={form.language} onChange={handleChange}>
            {languages.map((l) => (
              <option key={l}>{l}</option>
            ))}
          </select>
        </div>

        {/* TABS */}
        <div className="tabs">
          <button
            className={activeTab === "other" ? "active" : ""}
            onClick={() => setActiveTab("other")}
          >
            Other Details
          </button>
          <button
            className={activeTab === "address" ? "active" : ""}
            onClick={() => setActiveTab("address")}
          >
            Address
          </button>
          <button
            className={activeTab === "contacts" ? "active" : ""}
            onClick={() => setActiveTab("contacts")}
          >
            Contact Persons
          </button>
          <button
            className={activeTab === "bank" ? "active" : ""}
            onClick={() => setActiveTab("bank")}
          >
            Bank Details
          </button>
          <button
            className={activeTab === "documents" ? "active" : ""}
            onClick={() => setActiveTab("documents")}
          >
            Documents
          </button>
          <button
            className={activeTab === "remarks" ? "active" : ""}
            onClick={() => setActiveTab("remarks")}
          >
            Remarks
          </button>
        </div>

        <div className="tab-content">
          {/* OTHER DETAILS - Same as before */}
          {activeTab === "other" && (
            <>
              <div className="form-row">
                <label>PAN</label>
                <input
                  type="text"
                  name="pan"
                  value={form.pan}
                  onChange={handleChange}
                  placeholder="ABCDE1234F"
                />
              </div>
              <div className="form-row">
                <label>Currency</label>
                <select
                  name="currency"
                  value={form.currency}
                  onChange={handleChange}
                >
                  {currencies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} - {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>Accounts Payable</label>
                <input
                  type="text"
                  name="accountsPayable"
                  value={form.accountsPayable}
                  onChange={handleChange}
                />
              </div>
              <div className="form-row">
                <label>Opening Balance</label>
                <div className="currency-input">
                  <span>{form.currency}</span>
                  <input
                    type="number"
                    name="openingBalance"
                    value={form.openingBalance}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <label>Payment Terms</label>
                <select
                  name="paymentTerms"
                  value={form.paymentTerms}
                  onChange={handleChange}
                >
                  <option>Due on Receipt</option>
                  <option>Net 15</option>
                  <option>Net 30</option>
                  <option>Net 60</option>
                </select>
              </div>
              <div className="form-row">
                <label>TDS</label>
                <select name="tds" value={form.tds} onChange={handleChange}>
                  {tdsOptions.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="form-row checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="enablePortal"
                    checked={form.enablePortal}
                    onChange={handleChange}
                  />{" "}
                  Allow portal access for this vendor
                </label>
              </div>
            </>
          )}

          {/* ADDRESS - Same */}
          {activeTab === "address" && (
            <div className="address-grid">
              <div className="address-col">
                <h3>Billing Address</h3>
                <div className="form-row">
                  <label>Attention</label>
                  <input
                    type="text"
                    name="attention"
                    value={form.billing.attention}
                    onChange={(e) => handleAddressChange("billing", e)}
                  />
                </div>
                <div className="form-row">
                  <label>Country/Region</label>
                  <select
                    name="country"
                    value={form.billing.country}
                    onChange={(e) => handleAddressChange("billing", e)}
                  >
                    {countries.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>Address</label>
                  <div className="address-lines">
                    <input
                      type="text"
                      placeholder="Street 1"
                      name="street1"
                      value={form.billing.street1}
                      onChange={(e) => handleAddressChange("billing", e)}
                    />
                    <input
                      type="text"
                      placeholder="Street 2"
                      name="street2"
                      value={form.billing.street2}
                      onChange={(e) => handleAddressChange("billing", e)}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={form.billing.city}
                    onChange={(e) => handleAddressChange("billing", e)}
                  />
                </div>
                <div className="form-row">
                  <label>State</label>
                  <select
                    name="state"
                    value={form.billing.state}
                    onChange={(e) => handleAddressChange("billing", e)}
                  >
                    {indiaStates.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>Pin Code</label>
                  <input
                    type="text"
                    name="pincode"
                    value={form.billing.pincode}
                    onChange={(e) => handleAddressChange("billing", e)}
                  />
                </div>
                <div className="form-row">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.billing.phone}
                    onChange={(e) => handleAddressChange("billing", e)}
                  />
                </div>
                <div className="form-row">
                  <label>Fax</label>
                  <input
                    type="text"
                    name="fax"
                    value={form.billing.fax}
                    onChange={(e) => handleAddressChange("billing", e)}
                  />
                </div>
              </div>

              <div className="address-col">
                <h3>
                  Shipping Address{" "}
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={copyBillingToShipping}
                  >
                    Copy Billing Address
                  </button>
                </h3>
                {/* Same fields as billing, just "shipping" */}
                <div className="form-row">
                  <label>Attention</label>
                  <input
                    type="text"
                    name="attention"
                    value={form.shipping.attention}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  />
                </div>
                <div className="form-row">
                  <label>Country/Region</label>
                  <select
                    name="country"
                    value={form.shipping.country}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  >
                    {countries.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>Address</label>
                  <div className="address-lines">
                    <input
                      type="text"
                      placeholder="Street 1"
                      name="street1"
                      value={form.shipping.street1}
                      onChange={(e) => handleAddressChange("shipping", e)}
                    />
                    <input
                      type="text"
                      placeholder="Street 2"
                      name="street2"
                      value={form.shipping.street2}
                      onChange={(e) => handleAddressChange("shipping", e)}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={form.shipping.city}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  />
                </div>
                <div className="form-row">
                  <label>State</label>
                  <select
                    name="state"
                    value={form.shipping.state}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  >
                    {indiaStates.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>Pin Code</label>
                  <input
                    type="text"
                    name="pincode"
                    value={form.shipping.pincode}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  />
                </div>
                <div className="form-row">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.shipping.phone}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  />
                </div>
                <div className="form-row">
                  <label>Fax</label>
                  <input
                    type="text"
                    name="fax"
                    value={form.shipping.fax}
                    onChange={(e) => handleAddressChange("shipping", e)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* CONTACT PERSONS */}
          {activeTab === "contacts" && (
            <div className="contact-section">
              <button
                type="button"
                className="add-contact-btn"
                onClick={addContact}
              >
                + Add Contact Person
              </button>
              {form.contacts.map((c, i) => (
                <div key={i} className="contact-row">
                  <select
                    value={c.salutation}
                    onChange={(e) =>
                      updateContact(i, "salutation", e.target.value)
                    }
                  >
                    <option>Mr.</option>
                    <option>Mrs.</option>
                    <option>Ms.</option>
                    <option>Miss</option>
                    <option>Dr.</option>
                  </select>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={c.firstName}
                    onChange={(e) =>
                      updateContact(i, "firstName", e.target.value)
                    }
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={c.lastName}
                    onChange={(e) =>
                      updateContact(i, "lastName", e.target.value)
                    }
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={c.email}
                    onChange={(e) => updateContact(i, "email", e.target.value)}
                  />
                  <input
                    type="tel"
                    placeholder="Work Phone"
                    value={c.workPhone}
                    onChange={(e) =>
                      updateContact(i, "workPhone", e.target.value)
                    }
                  />
                  <input
                    type="tel"
                    placeholder="Mobile"
                    value={c.mobile}
                    onChange={(e) => updateContact(i, "mobile", e.target.value)}
                  />
                  {form.contacts.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeContact(i)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* PERFECT BANK DETAILS TAB - EXACTLY LIKE YOUR SCREENSHOT */}
          {activeTab === "bank" && (
            <div className="bank-details-section">
              {form.bankAccounts.length === 0 ? (
                <p className="no-bank-msg">No bank accounts added yet.</p>
              ) : (
                form.bankAccounts.map((bank, index) => (
                  <div key={index} className="bank-block">
                    <div className="bank-header">
                      <h4>Bank {index + 1}</h4>
                      <button
                        type="button"
                        className="delete-bank-btn"
                        onClick={() => removeBankAccount(index)}
                      >
                        Delete
                      </button>
                    </div>
                    <div className="bank-form-grid">
                      <div className="form-row">
                        <label>Account Holder Name</label>
                        <input
                          type="text"
                          value={bank.holderName || ""}
                          readOnly
                          className="readonly-input"
                        />
                      </div>
                      <div className="form-row">
                        <label>Bank Name</label>
                        <input
                          type="text"
                          value={bank.bankName || ""}
                          readOnly
                          className="readonly-input"
                        />
                      </div>
                      <div className="form-row">
                        <label>Account Number*</label>
                        <input
                          type="password"
                          value="••••••••••••••••"
                          readOnly
                          className="readonly-input"
                        />
                      </div>
                      <div className="form-row">
                        <label>Re-enter Account Number*</label>
                        <input
                          type="text"
                          value={bank.accountNumber || ""}
                          readOnly
                          className="readonly-input"
                        />
                      </div>
                      <div className="form-row">
                        <label>IFSC*</label>
                        <input
                          type="text"
                          value={bank.ifsc || ""}
                          readOnly
                          className="readonly-input"
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}

              <button
                type="button"
                className="add-new-bank-btn"
                onClick={() => setShowBankForm(true)}
              >
                + Add New Bank
              </button>

              {showBankForm && (
                <div className="bank-modal-overlay">
                  <div className="bank-modal">
                    <h3>Add New Bank Account</h3>
                    {bankError && <div className="error-msg">{bankError}</div>}
                    <div className="form-row">
                      <label>Account Holder Name *</label>
                      <input
                        type="text"
                        name="holderName"
                        value={bankForm.holderName}
                        onChange={handleBankChange}
                        placeholder="Enter holder name"
                      />
                    </div>
                    <div className="form-row">
                      <label>Bank Name *</label>
                      <input
                        type="text"
                        name="bankName"
                        value={bankForm.bankName}
                        onChange={handleBankChange}
                        placeholder="e.g. HDFC Bank"
                      />
                    </div>
                    <div className="form-row">
                      <label>Account Number *</label>
                      <input
                        type="password"
                        name="accountNumber"
                        value={bankForm.accountNumber}
                        onChange={handleBankChange}
                        placeholder="Enter account number"
                      />
                    </div>
                    <div className="form-row">
                      <label>Re-enter Account Number *</label>
                      <input
                        type="text"
                        name="reAccountNumber"
                        value={bankForm.reAccountNumber}
                        onChange={handleBankChange}
                        placeholder="Re-type account number"
                      />
                    </div>
                    <div className="form-row">
                      <label>IFSC Code *</label>
                      <input
                        type="text"
                        name="ifsc"
                        value={bankForm.ifsc}
                        onChange={handleBankChange}
                        placeholder="e.g. HDFC0000123"
                      />
                    </div>
                    <div className="modal-actions">
                      <button className="save-btn" onClick={addBankAccount}>
                        Save Bank Account
                      </button>
                      <button
                        className="cancel-btn"
                        onClick={() => {
                          setShowBankForm(false);
                          setBankError("");
                          setBankForm({
                            holderName: "",
                            bankName: "",
                            accountNumber: "",
                            reAccountNumber: "",
                            ifsc: "",
                          });
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* DOCUMENTS & REMARKS - Same */}
          {activeTab === "documents" && (
            <div className="documents-section">
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileUpload}
                style={{ display: "none" }}
                id="file-upload"
              />
              <label htmlFor="file-upload" className="upload-btn">
                + Upload Documents (Max 10 files, 10MB each)
              </label>
              {form.documents.length > 0 && (
                <div className="documents-list">
                  {form.documents.map((file, i) => (
                    <div key={i} className="file-item">
                      <span>{file}</span>
                      <button
                        type="button"
                        className="remove-btn small"
                        onClick={() => removeDocument(i)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "remarks" && (
            <div className="form-row">
              <label>Remarks (Internal Notes)</label>
              <textarea
                rows="6"
                name="remarks"
                value={form.remarks}
                onChange={handleChange}
                placeholder="Add any internal notes..."
              />
            </div>
          )}
        </div>

        <div className="form-actions">
          <button className="save-btn" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            className="cancel-btn"
            onClick={() => navigate("/purchase/vendors")}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorsData;
